import { ProductDataProcessor, ProductRenderer, buildColorCodeToLabelMap } from '@theme/searchspring-core';
import { CONSTANTS as SEARCHSPRING_CONSTANTS } from '@theme/searchspring-core';
import { Component } from '@theme/component';
import { escapeHtml } from '@theme/text-utils';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
import { getArrowIcon } from '@theme/icons';

export const CONSTANTS = {
  LOCAL_STORAGE_KEYS: {
    USER_ID: 'ssUserId',
    GRID_LAYOUT: 'four-col',
  },
  SESSION_STORAGE_KEYS: {
    SESSION_ID: 'ssSessionId',
  },
  PRODUCT_TYPES: SEARCHSPRING_CONSTANTS.PRODUCT_TYPES,
  FILTER_FIELDS: SEARCHSPRING_CONSTANTS.FILTER_FIELDS,
};

/**
 * Base class for Searchspring results components.
 * Provides common functionality for search and collection pages.
 * @abstract
 */
export class SearchspringResultsBase extends Component {
  /** @type {Array<{field: string, values: string[]}>} */
  appliedFilters = [];

  pagination = {
    totalResults: 0,
    begin: 0,
    end: 0,
    currentPage: 1,
    totalPages: 1,
    previousPage: 1,
    nextPage: 1,
    perPage: 24,
    defaultPerPage: 24,
  };

  /** @type {{ field: string | null, direction: string | null }} */
  sorting = {
    field: null,
    direction: null,
  };

  /** @type {Map<string, any>} */
  requestCache = new Map();

  /** @type {any[] | null} */
  sortingOptions = null;

  /** @type {boolean} */
  isMobile = isMobileBreakpoint();

  /** @type {string} */
  siteId = '';

  /** @type {any[] | null} */
  banners = null;

  /** @type {Record<string, string[]>} */
  colorGroupsMap = {};

  /** @type {Map<string, string>} */
  colorCodeToLabelMap = new Map();

  /**
   * Generate a UUID v4 for tracking
   * @returns {string}
   */
  static generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Returns the URL with Searchspring tracking params (userId, sessionId,
   * pageLoadId, domain) removed, used as the cache key for prefetched
   * responses on `window.__ssPrefetch`.
   * @param {string} url
   * @returns {string}
   */
  static stripTrackingParams(url) {
    try {
      const u = new URL(url);
      u.searchParams.delete('userId');
      u.searchParams.delete('sessionId');
      u.searchParams.delete('pageLoadId');
      u.searchParams.delete('domain');
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  /**
   * Builds the filter query string from applied filters.
   * @param {Array<{field: string, values: string[]}>} appliedFilters
   * @returns {string}
   */
  static buildFilterQuery(appliedFilters) {
    if (appliedFilters.length === 0) return '';

    const sortedFilters = appliedFilters.slice().sort((a, b) => a.field.localeCompare(b.field));

    const filterString = sortedFilters
      .map((filter) => {
        return filter.values
          .slice()
          .sort()
          .map((value) => `filter.${filter.field}=${encodeURIComponent(value)}`)
          .join('&');
      })
      .join('&');

    const appliedTypeFilter = appliedFilters.find((f) => f.field === CONSTANTS.FILTER_FIELDS.AVAILABILITY)?.values || ['*'];
    const appliedColorFilter = appliedFilters.find((f) => f.field === CONSTANTS.FILTER_FIELDS.COLORS)?.values || ['*'];
    const appliedSizeFilter = appliedFilters.find((f) => f.field === CONSTANTS.FILTER_FIELDS.SIZES)?.values || ['*'];
    const typeColorSizeFilters = [];

    for (const type of appliedTypeFilter) {
      // @ts-ignore
      const typeFromAvailability = SEARCHSPRING_CONSTANTS.AVAILIBILITY_TYPES[type];
      const typeFilter = typeFromAvailability ? `${typeFromAvailability}-` : `${type}-`;

      for (const color of appliedColorFilter) {
        const colorCode = color.indexOf(':') === -1 ? color : color.split(':')[0];

        for (const size of appliedSizeFilter) {
          if (size === '*' && colorCode === '*' && type === '*') {
            continue; // skip the catch-all filter
          }
          typeColorSizeFilters.push(`${typeFilter}${colorCode}-${size}`);
        }
      }
    }

    let bgFilterString = '';
    if (typeColorSizeFilters.length > 0) {
      bgFilterString = typeColorSizeFilters.map((tcs) => `bgfilter.${CONSTANTS.FILTER_FIELDS.TYPE_COLOR_SIZES}=${encodeURIComponent(tcs)}`).join('&');
    }

    return `&${filterString}${bgFilterString ? `&${bgFilterString}` : ''}`;
  }

  /**
   * Builds the pagination query string.
   * @param {number} currentPage
   * @returns {string}
   */
  static buildPaginationQuery(currentPage) {
    return currentPage === 1 ? '' : `&page=${currentPage}`;
  }

  /**
   * Builds the sort query string.
   * @param {{ field: string | null, direction: string | null }} sorting
   * @returns {string}
   */
  static buildSortQuery(sorting) {
    if (!sorting.field || !sorting.direction) return '';
    return `&sort.${sorting.field}=${sorting.direction}`;
  }

  /**
   * Build the API URL. Must be implemented by subclasses.
   * @abstract
   * @returns {string}
   */
  buildApiUrl() {
    throw new Error('buildApiUrl must be implemented by subclass');
  }

  /**
   * Get the results count label. Must be implemented by subclasses.
   * @abstract
   * @returns {string}
   */
  getResultsCountLabel() {
    throw new Error('getResultsCountLabel must be implemented by subclass');
  }

  /**
   * Check if the component has required data to fetch. Must be implemented by subclasses.
   * @abstract
   * @returns {boolean}
   */
  hasRequiredData() {
    throw new Error('hasRequiredData must be implemented by subclass');
  }

  async connectedCallback() {
    super.connectedCallback();

    // Require siteId to be provided via data-site-id attribute on the element
    this.siteId = this.dataset?.siteId || '';
    if (!this.siteId) {
      console.warn('SearchspringResultsBase: missing data-site-id attribute');
      return;
    }

    this.#loadColorGroups();
    this.#loadColorLabels();

    this.#getInitialFiltersFromUrl();
    this.#getPaginationFromUrl();
    this.#getSortingFromUrl();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);

    this.#loadGridLayout();
    this.#setupPerPageSelect();
    this.#setupModalListeners();

    if (!this.hasRequiredData()) {
      console.warn('SearchspringResultsBase: Missing required data.');
      return;
    }

    this.showSkeletonLoading();

    const productsWithOptions = await this.fetchProducts();
    if (productsWithOptions.length === 0) {
      this.#renderEmptyState();
      return;
    }
    await this.renderProduct(productsWithOptions);
  }

  #setupPerPageSelect() {
    const select = this.querySelector('.searchspring-results__per-page');
    if (!select) return;
    select.value = String(this.pagination.perPage);
    select.addEventListener('change', (e) => {
      this.pagination.perPage = parseInt(e.target.value, 10);
      this.pagination.currentPage = 1;
      this.updatePaginationUrl();
      this.#updateUrlWithFilters();
      this.#updateUrlWithSorting();
      this.requestCache.clear();
      this.onFilterChanged();
    });
  }

  #setupModalListeners() {
    const expandButton = this.querySelector('.searchspring-results__expand-filters-button');
    const mobileFilters = this.querySelector('.searchspring-results__filters--mobile');
    const closeButton = this.querySelector('.modal-close');
    const overlay = this.querySelector('.modal-overlay');

    if (expandButton && mobileFilters) {
      expandButton.addEventListener('click', () => {
        mobileFilters.classList.toggle('active');
        this.#toggleScrollLock(mobileFilters.classList.contains('active'));
      });
    }

    if (closeButton && mobileFilters) {
      closeButton.addEventListener('click', () => {
        mobileFilters.classList.remove('active');
        this.#toggleScrollLock(false);
      });
    }

    if (overlay && mobileFilters) {
      overlay.addEventListener('click', () => {
        mobileFilters.classList.remove('active');
        this.#toggleScrollLock(false);
      });
    }

    const applyButton = this.querySelector('.modal-footer .apply');
    const clearAllButton = this.querySelector('.modal-footer .clear-all');

    if (applyButton && mobileFilters) {
      applyButton.addEventListener('click', () => {
        this.updatePaginationUrl();
        this.#updateUrlWithFilters();
        this.#updateUrlWithSorting();
        this.onFilterChanged();
        mobileFilters.classList.remove('active');
        this.#toggleScrollLock(false);
      });
    }

    if (clearAllButton && mobileFilters) {
      clearAllButton.addEventListener('click', () => {
        this.onClearAllFilters();
        mobileFilters.classList.remove('active');
        this.#toggleScrollLock(false);
      });
    }
  }

  /**
   * Toggles scroll-lock attribute on the HTML element.
   * @param {boolean} isLocked
   */
  #toggleScrollLock(isLocked) {
    if (isLocked) {
      document.documentElement.setAttribute('scroll-lock', '');
    } else {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  #renderEmptyState() {
    const container = this.querySelector('.searchspring-product-grid-container');
    const noResultsMessage = this.querySelector('.search-results__no-results');

    if (container) {
      container.innerHTML = `
        <div class="searchspring-empty-state">
          <h2>No products found.</h2>
        </div>
      `;
    }

    if (noResultsMessage instanceof HTMLElement) {
      noResultsMessage.classList.remove('hidden');
    }

    this.classList.add('products-empty');

    this.renderPagination();
  }

  /**
   * Process API response data. Can be overridden by subclasses.
   * @param {any[]} products
   * @returns {any[]}
   */
  processProducts(products) {
    const productProcessor = new ProductDataProcessor();
    const formattedProducts = productProcessor.formatProducts(products);
    // Attach per-type prices so product cards can filter swatches by availability
    for (const product of formattedProducts) {
      const rawPrices = productProcessor.parsePrices(product);
      const formatted = productProcessor.formatPriceRanges(rawPrices);
      product.mtoPrices = productProcessor.filterPricesByType(formatted, CONSTANTS.PRODUCT_TYPES.MTO);
      product.rtsPrices = productProcessor.filterPricesByType(formatted, CONSTANTS.PRODUCT_TYPES.RTS);
    }
    return productProcessor.mapProductOptions(formattedProducts);
  }

  /**
   * Fetches products from the Searchspring API.
   * @returns {Promise<any[]>}
   */
  async fetchProducts() {
    const url = this.buildApiUrl();

    if (this.requestCache.has(url)) {
      const cached = this.requestCache.get(url);
      const userPerPage = this.pagination.perPage;
      this.pagination = { ...cached.pagination, perPage: userPerPage };
      this.setFilterData({ facets: cached.facets });
      this.updateResultsCount();
      return cached.products;
    }

    try {
      const canonical = SearchspringResultsBase.stripTrackingParams(url);
      const prefetched = typeof window !== 'undefined' && window.__ssPrefetch
        ? window.__ssPrefetch[canonical]
        : null;

      let data;
      let fromPrefetch = false;
      if (prefetched) {
        delete window.__ssPrefetch[canonical];
        data = await prefetched;
        fromPrefetch = Boolean(data);
      }
      if (!data) {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`API request failed: ${response.status}`);
        }
        data = await response.json();
      }

      // The prefetch can resolve before sibling module scripts
      // (searchspring-search-filters.js, searchspring-product-card.js) have
      // evaluated and registered their custom elements. Wait so
      // setFilterData/renderProducts find upgraded elements rather than
      // no-op'ing on plain HTMLElement.
      if (fromPrefetch) {
        await Promise.all([
          customElements.whenDefined('searchspring-search-filters'),
          customElements.whenDefined('searchspring-product-card'),
        ]);
      }

      const sorting = data?.sorting?.options || [];
      if (sorting.length > 0) {
        this.sortingOptions = sorting;
        this.updateSortingOptions(sorting);
      }

      const userPerPage = this.pagination.perPage;
      this.pagination = { ...data.pagination, perPage: userPerPage };
      this.setFilterData(data);
      this.updateResultsCount();

      const products = data.results;
      const productsWithOptions = this.processProducts(products);

      // Extract banners from merchandising content
      const banners = this.#extractBanners(data);
      if (banners.length > 0) {
        this.banners = banners;
      }

      const cachedObject = {
        products: productsWithOptions,
        pagination: data.pagination,
        facets: data.facets,
        banners: banners,
      };
      this.requestCache.set(url, cachedObject);

      return productsWithOptions;
    } catch (error) {
      console.error('Searchspring API error:', error);
      return [];
    }
  }

  /**
   * Handles changes to filters.
   * @param {string} field
   * @param {string} value
   * @param {boolean} checked
   */
  onChangeFilter(field, value, checked) {
    const currentFilter = this.appliedFilters.find((filter) => filter.field === field);

    if (!currentFilter) {
      if (checked) {
        this.appliedFilters.push({ field, values: [value] });
      }
    } else {
      if (checked) {
        if (!currentFilter.values.includes(value)) {
          currentFilter.values.push(value);
        }
      } else {
        currentFilter.values = currentFilter.values.filter((v) => v !== value);
        if (currentFilter.values.length === 0) {
          this.appliedFilters = this.appliedFilters.filter((f) => f.field !== field);
        }
      }
    }

    if (!this.isMobile) {
      this.pagination.currentPage = 1;
      this.updatePaginationUrl();
      this.#updateUrlWithFilters();
      this.onFilterChanged();
    } else {
      this.pagination.currentPage = 1;
    }
  }

  /**
   * Clears all filters for a specific field.
   * @param {string} field
   */
  onClearAllFilter(field) {
    this.appliedFilters = this.appliedFilters.filter((f) => f.field !== field);
    this.pagination.currentPage = 1;
    this.updatePaginationUrl();
    this.#updateUrlWithFilters();
    this.onFilterChanged();
  }

  /**
   * Clears all filters.
   */
  onClearAllFilters() {
    this.appliedFilters = [];
    this.pagination.currentPage = 1;
    this.updatePaginationUrl();
    this.#updateUrlWithFilters();
    this.onFilterChanged();
  }

  #getInitialFiltersFromUrl() {
    const url = new URL(window.location.href);
    const filterParams = url.searchParams.getAll('filter');

    this.appliedFilters = [];

    filterParams.forEach((filterParam) => {
      const colonIndex = filterParam.indexOf(':');
      if (colonIndex === -1) return;

      const field = filterParam.substring(0, colonIndex);
      const value = filterParam.substring(colonIndex + 1);

      if (field && value) {
        const existingFilter = this.appliedFilters.find((filter) => filter.field === field);

        if (existingFilter) {
          if (!existingFilter.values.includes(value)) {
            existingFilter.values.push(value);
          }
        } else {
          this.appliedFilters.push({
            field: field,
            values: [value],
          });
        }
      }
    });
  }

  /**
   * Default "Best Match" sort option. Empty field/direction means no sort is
   * sent to the API and no client-side sort is applied — merchandising order
   * is preserved.
   */
  static BEST_MATCH_OPTION = { field: '', direction: '', label: 'Best Match' };

  /**
   * Updates sorting options on sort components. Prepends a "Best Match"
   * option that, when selected, clears sorting.
   * @param {any[]} sorting
   */
  updateSortingOptions(sorting) {
    const sortComponents = this.querySelectorAll('searchspring-sort-by');
    const optionsWithDefault = [SearchspringResultsBase.BEST_MATCH_OPTION, ...sorting];

    if (!this.sorting.direction || !this.sorting.field) {
      this.sorting = { ...SearchspringResultsBase.BEST_MATCH_OPTION };
    }

    sortComponents.forEach((sortComponent) => {
      if (
        sortComponent instanceof HTMLElement &&
        typeof /** @type {any} */ (sortComponent).updateSearchingOptions === 'function'
      ) {
        /** @type {any} */ (sortComponent).updateSearchingOptions(optionsWithDefault, this.sorting);
      }
    });
  }

  #updateUrlWithFilters() {
    const url = new URL(window.location.href);
    url.searchParams.delete('filter');

    this.appliedFilters.forEach((filter) => {
      filter.values.forEach((value) => {
        url.searchParams.append('filter', `${filter.field}:${value}`);
      });
    });

    window.history.replaceState({}, '', url);
  }

  #getPaginationFromUrl() {
    const url = new URL(window.location.href);
    const pageParam = url.searchParams.get('page');
    const pageNumber = pageParam ? parseInt(pageParam, 10) : 1;

    if (!isNaN(pageNumber) && pageNumber > 0) {
      this.pagination.currentPage = pageNumber;
    } else {
      this.pagination.currentPage = 1;
    }

    const perPageParam = url.searchParams.get('perPage');
    if (perPageParam) {
      const perPage = parseInt(perPageParam, 10);
      if (!isNaN(perPage) && perPage > 0) {
        this.pagination.perPage = perPage;
      }
    }
  }

  #getSortingFromUrl() {
    const url = new URL(window.location.href);
    const sortParams = url.searchParams;

    for (const [key, value] of sortParams) {
      if (key.startsWith('sort.')) {
        const field = key.substring(5);
        this.sorting = { field, direction: value };
        this.onSortingLoadedFromUrl();
        break;
      }
    }
  }

  /**
   * Called when sorting is loaded from URL. Can be overridden by subclasses.
   */
  onSortingLoadedFromUrl() {
    // Override in subclass if needed
  }

  /**
   * Called when user changes sorting. Can be overridden by subclasses.
   */
  onUserChangedSorting() {
    // Override in subclass if needed
  }

  updatePaginationUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('perPage');

    if (this.pagination.currentPage > 1) {
      url.searchParams.append('page', this.pagination.currentPage.toString());
    }

    if (this.pagination.perPage !== this.pagination.defaultPerPage) {
      url.searchParams.append('perPage', this.pagination.perPage.toString());
    }

    window.history.replaceState({}, '', url);
  }

  #updateUrlWithSorting() {
    const url = new URL(window.location.href);

    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('sort.')) {
        url.searchParams.delete(key);
      }
    }

    if (this.sorting.field && this.sorting.direction) {
      url.searchParams.append(`sort.${this.sorting.field}`, this.sorting.direction);
    }

    window.history.replaceState({}, '', url);
  }

  /**
   * Handles sorting changes.
   * @param {{ field: string, direction: string }} activeOption
   */
  onChangeSorting(activeOption) {
    this.sorting.field = activeOption.field;
    this.sorting.direction = activeOption.direction;

    // Call onUserChangedSorting() if implemented by subclass
    this.onUserChangedSorting();

    if (this.sortingOptions) {
      this.updateSortingOptions(this.sortingOptions);
    }

    if (!this.isMobile) {
      this.pagination.currentPage = 1;
      this.updatePaginationUrl();
      this.#updateUrlWithSorting();
      this.onFilterChanged();
    } else {
      this.pagination.currentPage = 1;
    }
  }

  /**
   * Handles filter changes by re-fetching and re-rendering.
   */
  async onFilterChanged() {
    this.showSkeletonLoading();
    const productsWithOptions = await this.fetchProducts();
    if (productsWithOptions.length === 0) {
      this.#renderEmptyState();
      return;
    }
    await this.renderProduct(productsWithOptions);
  }

  /**
   * Parse color groups mapping from data-color-groups attribute.
   */
  #loadColorGroups() {
    const attr = this.getAttribute('data-color-groups');
    if (!attr) return;
    try {
      const raw = JSON.parse(attr);
      for (const [key, codes] of Object.entries(raw)) {
        this.colorGroupsMap[key.toLowerCase()] = /** @type {string[]} */ (codes);
      }
    } catch (e) {
      console.warn('Failed to parse color groups:', e);
    }
  }

  /**
   * Parse color labels and build code→label map from data-color-labels attribute.
   */
  #loadColorLabels() {
    const attr = this.getAttribute('data-color-labels');
    if (!attr) return;
    try {
      const colorLabelsArr = JSON.parse(attr);
      this.colorCodeToLabelMap = buildColorCodeToLabelMap(colorLabelsArr);
    } catch (e) {
      // ignore parse errors
    }
  }

  /**
   * Gets active color filter values from applied filters,
   * @returns {string[]}
   */
  getActiveColorFilters() {
    const colorFilter = this.appliedFilters.find(
      (filter) => filter.field === CONSTANTS.FILTER_FIELDS.COLORS
    );
    const colorValues = colorFilter?.values || [];

    const colorGroupFilter = this.appliedFilters.find(
      (filter) => filter.field === CONSTANTS.FILTER_FIELDS.COLOR_GROUPS
    );
    if (!colorGroupFilter || colorGroupFilter.values.length === 0) {
      return colorValues;
    }

    const resolvedCodes = colorGroupFilter.values.flatMap(
      (groupName) => this.colorGroupsMap[groupName.toLowerCase()] || []
    );

    // Direct color filters go last so that reverse-iteration in
    // findMatchingColorFromFilters prefers them over group-resolved codes.
    return [...resolvedCodes, ...colorValues];
  }

  /**
   * Gets active availability filter value from applied filters.
   * @returns {string | null}
   */
  getActiveAvailabilityFilter() {
    const availabilityFilter = this.appliedFilters.find(
      (filter) => filter.field === 'mfield_searchspring_availability'
    );
    return availabilityFilter?.values?.[0] || null;
  }

  /**
   * Gets active size filter value from applied filters.
   * @returns {string | null}
   */
  getActiveSizeFilter() {
    const sizeFilter = this.appliedFilters.find(
      (filter) => filter.field === 'mfield_searchspring_sizes'
    );
    return sizeFilter?.values?.[0] || null;
  }

  /**
   * Extract banners from API response merchandising content.
   * @param {any} data - API response data
   * @returns {any[]} Array of banners with position information
   */
  #extractBanners(data) {
    /** @type {any[]} */
    const banners = [];

    if (!data?.merchandising?.content?.inline) {
      return banners;
    }

    const inlineContent = data.merchandising.content.inline;

    if (Array.isArray(inlineContent)) {
      inlineContent.forEach((item) => {
        if (item?.config?.position?.index !== undefined) {
          banners.push({
            ...item,
            positionIndex: item.config.position.index,
          });
        }
      });
    }

    // Sort banners by position index
    banners.sort((a, b) => a.positionIndex - b.positionIndex);

    return banners;
  }

  /**
   * Renders the products in the grid container.
   * @param {any[]} products
   * @param {boolean} [append=false]
   */
  async renderProduct(products, append = false) {
    const container = this.querySelector('.searchspring-product-grid-container');
    const noResultsMessage = this.querySelector('.search-results__no-results');
    if (!container) return;

    const activeColorFilters = this.getActiveColorFilters();
    const activeAvailabilityFilter = this.getActiveAvailabilityFilter();
    const activeSizeFilter = this.getActiveSizeFilter();
    const productRenderer = new ProductRenderer(/** @type {HTMLElement} */(container));
    await productRenderer.renderProducts(products, false, append, { activeColorFilters, activeAvailabilityFilter, activeSizeFilter, banners: this.banners || [], colorCodeToLabelMap: this.colorCodeToLabelMap });

    if (noResultsMessage instanceof HTMLElement) {
      noResultsMessage.classList.add('hidden');
    }

    this.classList.remove('products-empty');

    this.renderPagination();
  }

  updateResultsCount() {
    const resultsCountElement = this.querySelector('.results-count');
    if (resultsCountElement) {
      resultsCountElement.textContent = this.getResultsCountLabel();
    }
  }

  /**
   * Renders standard pagination controls (prev/page-numbers/next) into
   * the `.searchspring-pagination` container. Hides the container when
   * there is only a single page of results.
   */
  renderPagination() {
    const container = this.querySelector('.searchspring-pagination');
    if (!(container instanceof HTMLElement)) return;

    const { currentPage, totalPages } = this.pagination;

    if (!totalPages || totalPages <= 1) {
      container.setAttribute('hidden', '');
      container.innerHTML = '';
      return;
    }

    container.removeAttribute('hidden');

    const pages = SearchspringResultsBase.#buildPageList(currentPage, totalPages);
    const prevPage = Math.max(1, currentPage - 1);
    const nextPage = Math.min(totalPages, currentPage + 1);
    const prevDisabled = currentPage <= 1;
    const nextDisabled = currentPage >= totalPages;

    const items = pages.map((p) => {
      if (p === '…') {
        return '<li class="searchspring-pagination__ellipsis" aria-hidden="true">…</li>';
      }
      const isActive = p === currentPage;
      return `
        <li>
          <button
            type="button"
            class="searchspring-pagination__page${isActive ? ' is-active' : ''}"
            data-page="${p}"
            ${isActive ? 'aria-current="page"' : ''}
            aria-label="Page ${p}"
          >${p}</button>
        </li>`;
    }).join('');

    container.innerHTML = `
      <button
        type="button"
        class="searchspring-pagination__arrow searchspring-pagination__arrow--prev"
        data-page="${prevPage}"
        ${prevDisabled ? 'disabled' : ''}
        aria-label="Previous page"
      >${getArrowIcon()}</button>
      <ul class="searchspring-pagination__list">${items}</ul>
      <button
        type="button"
        class="searchspring-pagination__arrow"
        data-page="${nextPage}"
        ${nextDisabled ? 'disabled' : ''}
        aria-label="Next page"
      >${getArrowIcon()}</button>
    `;

    container.querySelectorAll('button[data-page]').forEach((button) => {
      button.addEventListener('click', (event) => {
        const target = /** @type {HTMLButtonElement} */ (event.currentTarget);
        if (target.hasAttribute('disabled')) return;
        const page = parseInt(target.getAttribute('data-page') || '', 10);
        if (!isNaN(page)) this.goToPage(page);
      });
    });
  }

  /**
   * Build a condensed list of page numbers with ellipses around the current page.
   * Always includes first and last pages. Inserts '…' placeholders when there is
   * a gap of more than one page.
   * @param {number} current
   * @param {number} total
   * @returns {(number | '…')[]}
   */
  static #buildPageList(current, total) {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    /** @type {(number | '…')[]} */
    const pages = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    if (start > 2) pages.push('…');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('…');

    pages.push(total);
    return pages;
  }

  /**
   * Navigate to a specific page. Replaces the current product grid
   * (does not append) and scrolls to the top of the grid.
   * @param {number} pageNumber
   */
  async goToPage(pageNumber) {
    if (!Number.isFinite(pageNumber)) return;
    if (pageNumber < 1 || pageNumber > this.pagination.totalPages) return;
    if (pageNumber === this.pagination.currentPage) return;

    this.pagination.currentPage = pageNumber;
    this.updatePaginationUrl();

    this.scrollToGridTop();
    this.showSkeletonLoading();

    const productsWithOptions = await this.fetchProducts();
    if (productsWithOptions.length === 0) {
      this.#renderEmptyState();
      return;
    }

    await this.renderProduct(productsWithOptions);
  }

  scrollToGridTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  /**
   * Gets filter data from API response.
   * @param {any} data
   * @returns {any[]}
   */
  getFilterData(data) {
    return data.facets || [];
  }

  /**
   * Sets filter data on filter components.
   * @param {any} data
   */
  setFilterData(data) {
    const filterData = this.getFilterData(data);

    const filterComponents = this.querySelectorAll('searchspring-search-filters');
    filterComponents.forEach((filterComponent) => {
      if (
        filterComponent instanceof HTMLElement &&
        typeof /** @type {any} */ (filterComponent).setData === 'function'
      ) {
        /** @type {any} */ (filterComponent).setData(filterData);
      }
    });
  }

  showSkeletonLoading(count = 8) {
    const container = this.querySelector('.searchspring-product-grid-container');
    const noResultsMessage = this.querySelector('.search-results__no-results');

    if (!container) return;

    const skeletonCard = `
      <div class="searchspring-skeleton-card">
        <div class="searchspring-skeleton-card__image skeleton-item"></div>
        <div class="searchspring-skeleton-card__title skeleton-item"></div>
        <div class="searchspring-skeleton-card__price skeleton-item"></div>
        <div class="searchspring-skeleton-card__swatches">
          <div class="searchspring-skeleton-card__swatch skeleton-item"></div>
          <div class="searchspring-skeleton-card__swatch skeleton-item"></div>
          <div class="searchspring-skeleton-card__swatch skeleton-item"></div>
        </div>
      </div>
    `;

    const skeletonGrid = `
      <div class="searchspring-product-grid searchspring-skeleton-grid">
        ${skeletonCard.repeat(count)}
      </div>
    `;

    if (noResultsMessage instanceof HTMLElement) {
      noResultsMessage.classList.add('hidden');
    }

    container.innerHTML = skeletonGrid;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);

    const mobileFilters = this.querySelector('.searchspring-results__filters--mobile');
    if (mobileFilters?.classList.contains('active')) {
      this.#toggleScrollLock(false);
    }
  }

  /**
   * Updates the layout based on the input target.
   * @param {{ target: EventTarget | null }} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    const gridColumnsCount = target.value;
    const grid = this.querySelector('.searchspring-product-grid-container');
    if (!grid) return;

    grid.setAttribute('data-columns', gridColumnsCount);
    this.#saveGridLayout(gridColumnsCount);
    this.#refreshCardPictures(grid);
  }

  /**
   * Re-renders picture elements on every product card in the grid so the browser
   * evaluates srcset with the updated column sizes. Simply updating the `sizes` DOM
   * attribute is insufficient — browsers won't re-request an already-loaded image.
   *
   * Deferred to the next animation frame so the CSS grid layout (driven by the
   * updated `data-columns` attribute) has fully resolved before we re-initialize
   * slideshow dimensions and pick the srcset candidate.
   * @param {Element} grid
   */
  #refreshCardPictures(grid) {
    requestAnimationFrame(() => {
      grid.querySelectorAll('searchspring-product-card').forEach((card) => {
        if (typeof /** @type {any} */ (card).refreshPictureSizes === 'function') {
          /** @type {any} */ (card).refreshPictureSizes();
        }
      });
    });
  }

  /**
   * @param {string} layout
   */
  #saveGridLayout(layout) {
    try {
      localStorage.setItem(CONSTANTS.LOCAL_STORAGE_KEYS.GRID_LAYOUT, layout);
    } catch (error) {
      console.warn('Failed to save grid layout to localStorage:', error);
    }
  }

  #loadGridLayout() {
    try {
      const savedLayout = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_KEYS.GRID_LAYOUT);
      const layout = savedLayout || 'four-col';

      const grid = this.querySelector('.searchspring-product-grid-container');
      if (grid) {
        grid.setAttribute('data-columns', layout);
      }

      const layoutInput = this.querySelector(`[value="${layout}"]`);
      if (layoutInput instanceof HTMLInputElement) {
        layoutInput.checked = true;
      }
    } catch (error) {
      console.warn('Failed to load grid layout from localStorage:', error);
    }
  }

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    this.isMobile = !event.matches;

    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    this.#setLayout(targetElement.value);
  };

  /**
   * @param {string} value
   */
  #setLayout(value) {
    const grid = this.querySelector('#searchspring-product-grid');
    if (!grid) return;

    grid.setAttribute('product-grid-view', value);
    this.#saveGridLayout(value);
  }
}

class SearchspringSortBy extends Component {
  sortingOptions = [];

  activeOption = {
    field: 'relevance',
    direction: 'desc',
    label: 'Best Match',
  };

  async connectedCallback() {
    super.connectedCallback();
    this.renderSortingOptions();
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleClickOutside);
  }

  updateSearchingOptions(options, activeSorting) {
    this.sortingOptions = options;
    this.activeOption = activeSorting;
    this.activeOption.label =
      this.sortingOptions.find(
        (option) =>
          option.field === activeSorting.field && option.direction === activeSorting.direction
      )?.label || this.activeOption.label;
    this.renderSortingOptions();
  }

  toggleSortingOptions() {
    this.classList.toggle('active');
  }

  handleClickOutside = (event) => {
    if (!this.contains(event.target)) {
      this.classList.remove('active');
    }
  };

  renderSortingOptions() {
    if (!this.sortingOptions || this.sortingOptions.length === 0) return;
    const isMobile = this.getAttribute('data-type') === 'mobile';

    const template = `
            <div class="searchspring-sort-by">
              <div class="searchspring-sort-by__current" on:click="/toggleSortingOptions">
                <div class="searchspring-sort-by__label">Sort by</div>
                <span>${escapeHtml(this.activeOption.label)}</span>
                <span class="svg-wrapper icon-caret icon-animated">
                  <svg width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none">
                    <path d="M4 7L9.35101 12.5118C9.98295 13.1627 11.017 13.1627 11.649 12.5118L17 7" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
                </span>
                </div>
                <div class="searchspring-sort-by__options">
                    ${this.sortingOptions
                      .map((option) => {
                        const escapedField = escapeHtml(option.field);
                        const escapedDirection = escapeHtml(option.direction);
                        const escapedLabel = escapeHtml(option.label);
                        return `<div class="form-check">
                      <label for="sort-by-${escapedField}-${escapedDirection}">
                        <div class="flex items-center gap-1.5 cursor-pointer">
                          <input on:change="/onSortOptionChange" type="radio" name="sort-by" id="sort-by-${escapedField}-${escapedDirection}" data-label="${escapedLabel}" data-field="${escapedField}" value="${escapedDirection}" ${this.activeOption.field === option.field && this.activeOption.direction === option.direction ? 'checked' : ''} />
                          <span class="svg-wrapper icon-checkmark sorting-filter__checkmark">
                            <svg width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none" class="icon-checkmark">
                              <path d="M5.5 10L7.85101 12.5118C8.48295 13.1627 9.51704 13.1627 10.149 12.5118L15.5 7" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path>
                            </svg>
                          </span>
                          <span class="searchspring-sort-by__option-label">${escapedLabel}</span>
                        </div>
                      </label>
                    </div>`;
                      })
                      .join('')}
                </div>
            </div>
        `;

    const mobileTemplate = `
            <div class="searchspring-sort-by">
                <div class="form-field">
                <label for="sort-by" class="form-label">Sort By</label>
                <div class="form-select-wrapper">
                    <select on:change="/onSortOptionChange" id="sort-by" class="form-select">
                        ${this.sortingOptions
                          .map((option) => {
                            const escapedField = escapeHtml(option.field);
                            const escapedDirection = escapeHtml(option.direction);
                            const escapedLabel = escapeHtml(option.label);
                            return `<option data-label="${escapedLabel}" data-field="${escapedField}" value="${escapedDirection}" ${this.activeOption.field === option.field && this.activeOption.direction === option.direction ? 'selected' : ''}>${escapedLabel}</option>`;
                          })
                          .join('')}
                    </select>
                    <svg class="form-select__icon" width="20" height="20" aria-hidden="true" focusable="false" viewBox="0 0 20 20" fill="none">
                      <path d="M4 7L9.35101 12.5118C9.98295 13.1627 11.017 13.1627 11.649 12.5118L17 7" stroke="currentColor" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"></path>
                    </svg>
                </div>
                </div>
            </div>
        `;

    this.innerHTML = isMobile ? mobileTemplate : template;
  }

  onSortOptionClick(event) {
    const field = event.target.getAttribute('data-field');
    const direction = event.target.getAttribute('data-direction');
    const label = event.target.getAttribute('data-label');
    this.activeOption = { field, direction, label };
    this.onSortOptionChange(event);
  }

  onSortOptionChange(event) {
    const isMobile = this.getAttribute('data-type') === 'mobile';
    const selectedValue = event.target.value;
    const field = isMobile
      ? event.target.selectedOptions[0].getAttribute('data-field')
      : event.target.getAttribute('data-field');
    const direction = selectedValue;
    const label = isMobile
      ? event.target.selectedOptions[0].getAttribute('data-label')
      : event.target.getAttribute('data-label');
    this.activeOption = { field, direction, label };
    this.dispatchSortOptionChange();
  }

  dispatchSortOptionChange() {
    const resultsComponent = this.closest('searchspring-search-results, searchspring-collection');
    if (resultsComponent && typeof resultsComponent.onChangeSorting === 'function') {
      resultsComponent.onChangeSorting(this.activeOption);
    }
  }
}

if (!customElements.get('searchspring-sort-by')) {
  customElements.define('searchspring-sort-by', SearchspringSortBy);
}
